// src/i18n/displayKeys.js
// The contract that keeps translation from corrupting data. Product data objects
// interleave copy with config in the same literal, e.g.
//   { id: 'beginner', label: 'New', sub: 'Just starting…', weights: {}, routes: 'A' }
// Only allowlisted keys are ever swapped. Everything else — ids, slugs, hrefs,
// icon names, scoring weights — passes through untouched.

// Checked FIRST and wins over DISPLAY_KEYS. `value`, `name`, `type`, and `role`
// appear in this codebase as both copy and config, so they are denied outright
// rather than guessed at; any genuine copy under those keys is handled with
// t() at the component instead. `role` specifically collides across:
// data/products/paneLabData.js (pane layout slot enum: 'portrait'/'row'/'hero'/
// 'side'/'feature'/'small'/'wide'/'cell'), data/quiz/runwayData.js (engine
// routing tag: 'primary-tool'/'scanner'/'bundle'), and
// features/chatbot/ChatWidget.jsx (message enum: 'user'/'assistant') — vs.
// genuine testimonial author role copy (e.g. "Swing trader") elsewhere.
export const NEVER_TRANSLATE_KEYS = new Set([
  'id', 'slug', 'href', 'url', 'to', 'src', 'icon', 'iconName', 'image',
  'sceneKey', 'weights', 'routes', 'stage', 'variant', 'key', 'type',
  'platform', 'className', 'style', 'color', 'value', 'name', 'videoId',
  'anchor', 'target', 'rel', 'preset', 'mode', 'role',
]);

// `a` and `b` are the two halves of a two-line "checkpoint" label rendered as
// stacked spans: { a: 'Liquidity', b: 'zones' } paints "Liquidity" over
// "zones". They are only ever used for that widget (homepage scanner section
// plus every PDP "What it can do for you" section) — nothing else in the data
// tree uses single-letter keys. The halves form one noun phrase, so they must
// be translated as a pair; see the checkpoint-pair note in glossary.md.
export const DISPLAY_KEYS = new Set([
  'title', 'subtitle', 'eyebrow', 'heading', 'subheading',
  'body', 'text', 'copy', 'description', 'sub', 'note', 'caption',
  'cta', 'ctaLabel', 'linkLabel', 'label', 'buttonText',
  'alt', 'imageAlt', 'ariaLabel', 'placeholder',
  'seoTitle', 'seoDescription',
  'question', 'answer', 'quote', 'author',
  'headline', 'subline', 'why', 'tierLabel',
  'titleLead', 'titleAccent', 'titleStart', 'titleGradient', 'titleAccentWord',
  'gradientWord', 'accentLabel', 'blurb', 'sectionEyebrow', 'navLabel', 'tag',
  'heroTitle', 'heroKicker', 'metaLine', 'chromeLabel', 'disclaimer',
  'heroLead', 'kicker', 'brief', 'read', 'badge', 'cardTitle', 'cardText',
  'stripKicker', 'proofFoot',
  // The opt-in card's (CommunityOptInCard.jsx) own copy fields — email
  // capture placeholder/button/status text. Same card as `cardTitle`/
  // `cardText` above. Also read by the homepage community opt-in card
  // (data/homepage/homePageData.js, homePage2Data.js), so translating these
  // affects both; t() returns its input unchanged where no entry exists.
  'emailPlaceholder', 'emailLabel', 'emailButtonLabel', 'emailButtonSendingLabel',
  'successTitle', 'successText', 'errorText',
  // Prices are display copy, not config: German writes "797 €", English "€797".
  // t() returns its input when the catalog has no entry, so the other 27 PDPs
  // keep rendering their existing USD strings untouched.
  // `priceWas` is the struck-through regular price and `saveLabel` the discount
  // badge beside it (PriceCard.jsx) — same reasoning, same currency convention.
  'price', 'period', 'priceWas', 'saveLabel',
  'a', 'b',
]);

// `rotatingWords` entries are each a complete clause that finishes the hero H1
// (see homePageData.hero.heroLead for why they are not fragments).
export const SENTENCE_ARRAY_KEYS = new Set([
  'heroPoints', 'titleLines', 'bullets', 'points', 'items', 'lines', 'features',
  'rotatingWords',
]);

export function isDisplayKey(key) {
  if (NEVER_TRANSLATE_KEYS.has(key)) return false;
  return DISPLAY_KEYS.has(key);
}

export function isSentenceArrayKey(key) {
  if (NEVER_TRANSLATE_KEYS.has(key)) return false;
  return SENTENCE_ARRAY_KEYS.has(key);
}
